import { ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { retryFetch } from "@caredna-marlin/utils/client";
import { getLocalPreLoginEmail, resetLocalPreLoginEmail, useAuth } from "~/state/auth";
import { TokenResponse } from "@caredna-marlin/utils/auth";

type AuthError = {
  label: string,
  message: string
}

interface CustomLoginCallbackProps {
  loadingElement?: ReactNode;
  errorComponent?: (error: AuthError) => ReactNode;
}

export const CustomLoginCallback: React.FC<CustomLoginCallbackProps> = ({
  loadingElement = null,
  errorComponent = () => null,
}) => {
  const navigate = useNavigate();
  const handlingRedirect = useRef(false);
  const { loggedIn } = useAuth();

  // Check for code from cognito
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  // Check for error from cognito
  const urlHash = window.location.hash;
  const incomingHash = new URLSearchParams(urlHash.replace("#", ""))
  const errorLabel = incomingHash.get("error")
  const errorDescription = incomingHash.get("error_description")

  useEffect(() => {
    if (!handlingRedirect.current) {
      handlingRedirect.current = true;

      (async function () {
        try {
          if (code) {
            const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
            const preLoginEmail = getLocalPreLoginEmail();
            resetLocalPreLoginEmail();

            const response = await retryFetch(() => fetch(
              import.meta.env.VITE_SERVICE_HOST + "/token-exchange",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  code,
                  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
                  redirectUri: `${import.meta.env.VITE_WEB_HOST}/auth/cognito/callback`,
                  codeVerifier,
                  preLoginEmail,
                }),
                credentials: 'include',
              }
            ));
            const json = await response.json() as TokenResponse;

            const redirect = loggedIn(json);


            navigate(redirect, { replace: true });
          }
        } catch (e) {
          if (e instanceof Error) {
            return errorComponent({label: e.name, message: e.message});
          }
          return errorComponent({label: "unknown_error", message: "login failed"});
        }
      })();
    }
  }, [code, errorComponent, navigate]);

  if (errorLabel) {
    return errorComponent({label: errorLabel, message: errorDescription || ""});
  }

  return loadingElement;

};
